/**
 * RedactionToolbar Component
 *
 * Provides redaction tools for the PDF editor:
 * - Blackout tool (solid black)
 */

import { EyeOff, Trash2 } from 'lucide-react';
import type { RedactionTool } from '../../types';

interface RedactionToolbarProps {
  activeTool: RedactionTool;
  onToolChange: (tool: RedactionTool) => void;
  onDeleteSelected: () => void;
  hasSelection: boolean;
}

interface ToolButton {
  id: RedactionTool;
  icon: React.ReactNode;
  label: string;
  shortcut: string;
  description: string;
}

const tools: ToolButton[] = [
  {
    id: 'blackout',
    icon: <EyeOff className="w-4 h-4" />,
    label: 'Blackout',
    shortcut: 'X',
    description: 'Completely black out content',
  },
];

export function RedactionToolbar({
  activeTool,
  onToolChange,
  onDeleteSelected,
  hasSelection,
}: RedactionToolbarProps) {
  // Keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent) => {
    // Skip if user is typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    // Skip if modifier keys are pressed (except for Delete)
    if (e.key !== 'Delete' && e.key !== 'Backspace' && (e.ctrlKey || e.metaKey || e.altKey)) {
      return;
    }

    switch (e.key.toLowerCase()) {
      case 'x':
        onToolChange('blackout');
        break;
      case 'delete':
      case 'backspace':
        if (hasSelection) {
          e.preventDefault();
          onDeleteSelected();
        }
        break;
    }
  };

  // Set up keyboard shortcuts
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeyDown);
  }

  return (
    <div className="flex items-center gap-1">
      {tools.map((tool) => (
        <button
          key={tool.id}
          onClick={() => onToolChange(tool.id)}
          className={`flex items-center gap-2 px-3 py-2 rounded-[var(--radius-button)] transition-colors ${
            activeTool === tool.id
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-muted'
          }`}
          title={`${tool.description} (${tool.shortcut})`}
        >
          {tool.icon}
          <span className="text-sm hidden sm:inline">{tool.label}</span>
        </button>
      ))}

      <div className="w-px h-6 bg-border mx-2" />

      {/* Delete button */}
      <button
        onClick={onDeleteSelected}
        disabled={!hasSelection}
        className="flex items-center gap-2 px-3 py-2 hover:bg-destructive/10 text-destructive rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Delete selected redaction (Delete)"
      >
        <Trash2 className="w-4 h-4" />
        <span className="text-sm hidden sm:inline">Delete</span>
      </button>
    </div>
  );
}
